<template>
  <div class="checkbox-wrapper">
    <div class="checkbox">
      <input
        :id="checkboxId"
        v-model="internalValue"
        type="checkbox"
        :disabled="isDisabled"
        class="checkbox__input"
        v-bind="$attrs"
        @change="handleChange"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <label :for="checkboxId" class="checkbox__label">
        <span class="checkbox__custom"></span>
        <span v-if="label" class="checkbox__text">
          {{ label }}
        </span>
      </label>
    </div>

    <div v-if="hasError" class="checkbox__error">
      <div v-for="(error, index) in errorMessages" :key="index">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import { RandomId } from '@/shared/lib/random';

const { generateRandomId } = RandomId();

export default {
  name: 'Checkbox',
  props: {
    label: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number, Boolean],
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      checkboxId: this.generateRandomId('checkbox-'),
      isFocused: false,
      internalValue: this.value,
    };
  },
  watch: {
    value(newValue) {
      this.internalValue = newValue;
    },
    internalValue(newValue) {
      this.$emit('input', newValue);
    }
  },
  computed: {
    hasError() {
      return this.errorMessages.length > 0;
    }
  },
  methods: {
    generateRandomId,
    handleChange() {
      this.$emit('change', this.internalValue);
    },
    handleFocus() {
      this.isFocused = true;
      this.$emit('focus');
    },
    handleBlur() {
      this.isFocused = false;
      this.$emit('blur', this.internalValue);
    },
    focus() {
      this.$refs.checkbox?.focus();
    }
  }
};
</script>

<style scoped lang="scss">
.checkbox-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.checkbox {
  display: flex;
  align-items: center;

  &__input {
    display: none;

    &:checked + .checkbox__label .checkbox__custom {
      border-color: #FB923C;

      &::after {
        opacity: 1;
        transform: rotate(45deg) scale(1);
      }
    }

    &:focus + .checkbox__label .checkbox__custom {
      outline: 2px solid #FB923C;
      outline-offset: 2px;
    }

    &:disabled + .checkbox__label {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &--has-error + .checkbox__label .checkbox__custom {
      border-color: #f00;
    }
  }

  &__label {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }

  &__custom {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    min-width: 20px;
    border: 1px solid #D5D7DA;
    border-radius: 6px;
    background: white;
    transition: all 0.2s ease;
    margin-right: 8px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 6px;
      top: 3px;
      width: 5px;
      height: 10px;
      border: solid #FB923C;
      border-width: 0 2px 2px 0;
      opacity: 0;
      transform: rotate(45deg) scale(0);
      transition: all 0.2s ease;
    }
  }

  &__text {
    font-family: 'Geist', sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: #09090B;
  }

  &__error {
    color: #f00;
    font-size: 12px;
    margin-top: 4px;
  }
}
</style>
